import Alert from '@storeblocks/alert';
import { Button } from '@storeblocks/button';
import { Input } from '@storeblocks/input';
import Modal from '@storeblocks/modal';
import { Form as FormikForm, Formik, FormikHelpers } from 'formik';
import { type FC } from 'react';
import styled from 'styled-components';

import { useLazyOrganizationQuery } from '@/api/admin/adminApi';
import {
  useAddOrganizationMutation,
  useOrganizationsQuery,
} from '@/api/organizations/organizationsApi';
import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';
import { TrackingElement, useGetTrackId } from '@/tracking';

import {
  AddOrganizationFormValues,
  getInitialValues,
  getValidationSchema,
} from './AddOrganizationFormSchema';

interface Props {
  show: boolean;
  onClose: () => void;
}

export const AddOrganizationModal: FC<Props> = ({ show, onClose }) => {
  const organizationsQuery = useOrganizationsQuery();

  const [fetchOrganization, fetchOrganizationQuery] =
    useLazyOrganizationQuery();

  const [addOrganization, addOrganizationQuery] = useAddOrganizationMutation();

  const getTrackId = useGetTrackId();

  const texts = useTexts();

  const handleOnSubmit = async (
    values: AddOrganizationFormValues,
    formikHelpers: FormikHelpers<AddOrganizationFormValues>,
  ): Promise<void> => {
    await addOrganization({
      organizationId: values.organizationCmId,
      organizationName: fetchOrganizationQuery.data!.organizationName,
    });
    formikHelpers.resetForm();
  };

  const handleClosed = (resetForm: () => void): void => {
    organizationsQuery.refetch();
    addOrganizationQuery.reset();
    resetForm();
    onClose();
  };

  const handleClicked = (organizationCmId: string): void => {
    fetchOrganization({ organizationCmId });
  };
  return (
    <Formik
      initialValues={getInitialValues()}
      onSubmit={handleOnSubmit}
      validationSchema={getValidationSchema(texts, organizationsQuery.data!)}
    >
      {({ isSubmitting, errors, resetForm, values, getFieldProps }) => (
        <Modal
          trigger={undefined}
          open={show}
          onClose={() => {
            handleClosed(resetForm);
          }}
          title={texts.adminModal.newOrgLabel}
          content={
            addOrganizationQuery.isSuccess ? (
              <Alert
                fluid
                variant="success"
                title={texts.adminModal.addOrganization.success}
                description={fetchOrganizationQuery.data?.organizationName}
                data-test="add-organization-success-alert"
              />
            ) : (
              <div data-test="add-organization-modal">
                <FormikForm id="add-organization-form">
                  <WithGap>
                    <WithGap direction="row">
                      <Input
                        id="organizationCmId"
                        type="text"
                        fluid
                        label={texts.adminModal.orgID}
                        data-test="organization-cmid-input"
                        alert={{
                          messageType: errors.organizationCmId
                            ? 'error'
                            : undefined,
                          message: errors.organizationCmId,
                        }}
                        {...getFieldProps('organizationCmId')}
                      />
                      <StyledButton
                        onClick={() => {
                          handleClicked(values.organizationCmId);
                        }}
                        type="button"
                        variant="subtle"
                        loading={fetchOrganizationQuery.isFetching}
                        data-trackid={getTrackId(
                          'search-organization:add-organization-modal',
                          TrackingElement.Button,
                        )}
                        data-test="add-organization-search-button"
                      >
                        {texts.adminModal.searchButton}
                      </StyledButton>
                    </WithGap>

                    {fetchOrganizationQuery.isSuccess && (
                      <div>
                        {fetchOrganizationQuery.data
                          ?.isMissingOrganizationNumber && (
                          <Alert
                            fluid
                            variant="warning"
                            title={
                              texts.adminModal.addOrganization
                                .missingOrganizationNumber.title
                            }
                            data-test="add-organization-no-organization-number-alert"
                          >
                            <p>
                              {
                                texts.adminModal.addOrganization
                                  .missingOrganizationNumber.part1
                              }{' '}
                              <a
                                type="email"
                                href="mailto:team-connect@storebrand.no"
                              >
                                team-connect@storebrand.no
                              </a>
                            </p>
                            <p>
                              {
                                texts.adminModal.addOrganization
                                  .missingOrganizationNumber.part2
                              }
                            </p>
                          </Alert>
                        )}

                        <SpaceBetweenWithGap direction="row">
                          <div className="font-body-large">
                            {fetchOrganizationQuery.data?.organizationName}
                          </div>
                          <Button
                            type="submit"
                            loading={addOrganizationQuery.isLoading}
                            data-trackid={getTrackId(
                              'add-organization:add-organization-modal',
                              TrackingElement.Button,
                            )}
                            data-test="add-organization-submit-button"
                          >
                            {texts.adminModal.addButton}
                          </Button>
                        </SpaceBetweenWithGap>
                      </div>
                    )}

                    {fetchOrganizationQuery.isError && (
                      <Alert
                        fluid
                        variant="error"
                        title={texts.adminModal.addOrganization.notFound}
                        description={
                          texts.adminModal.addOrganization.notFoundDescription
                        }
                        data-test="add-organization-notfound-alert"
                      />
                    )}

                    {addOrganizationQuery.isError && (
                      <Alert
                        fluid
                        variant="error"
                        title={texts.adminModal.addOrganization.addError}
                        data-test="add-organization-error-alert"
                      />
                    )}
                  </WithGap>
                </FormikForm>
              </div>
            )
          }
          footer={
            <Button
              type="button"
              disabled={isSubmitting}
              variant="subtle"
              onClick={() => handleClosed(resetForm)}
              data-trackid={getTrackId(
                'close:add-organization-modal',
                TrackingElement.Button,
              )}
              data-test="add-organization-close-button"
            >
              {texts.adminModal.closeButton}
            </Button>
          }
        ></Modal>
      )}
    </Formik>
  );
};

const StyledButton = styled(Button)`
  /* Align the button with the input field. */
  margin-top: 32px;
`;

const SpaceBetweenWithGap = styled(WithGap)`
  border-top: 1px solid var(--p-color-stroke-subtle-0);
  padding-top: 24px;
  justify-content: space-between;
  align-items: center;
`;
